import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Stack, Plus, Trash, UploadSimple } from "@phosphor-icons/react";

const empty = { code: "", layer: "", block: "", description: "" };

export const SymbolLibrary = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const csvInput = useRef();

  const load = () => api.symbols().then(setItems);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.code.trim()) { toast.error("Code is required."); return; }
    try { await api.addSymbol(form); setForm(empty); await load(); toast.success(`Added ${form.code.toUpperCase()}`); }
    catch { toast.error("Could not add symbol."); }
  };
  const del = async (id, code) => { await api.deleteSymbol(id); await load(); toast.success(`Removed ${code}`); };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const t = toast.loading(`Importing ${file.name}…`);
    try { const r = await api.importSymbols(file); await load(); toast.success(`Imported ${r.imported} symbols`, { id: t }); }
    catch { toast.error("Import failed. Expect columns: code,layer,block,description", { id: t }); }
    finally { e.target.value = ""; }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="symbol-library-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Stack size={14} weight="bold" /> Symbols
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="symbol-library-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Stack size={18} weight="bold" className="text-[var(--primary)]" />Survey Symbol Library</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Manage the code → layer / block / description mappings used by Field-to-Finish generation.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-[var(--text-dim)]">Every Field-to-Finish generation pulls these exact code → layer / block / description mappings.</p>
          <input type="file" accept=".csv,.txt" ref={csvInput} className="hidden" onChange={importCsv} data-testid="sym-csv-input" />
          <Button size="sm" variant="outline" onClick={() => csvInput.current.click()} data-testid="sym-import-btn"
            className="h-7 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)] shrink-0">
            <UploadSimple size={13} /> Import CSV
          </Button>
        </div>
        <div className="grid grid-cols-[1fr_1.2fr_1.2fr_1.6fr_auto] gap-2 items-center">
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CODE" data-testid="sym-code" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Input value={form.layer} onChange={(e) => setForm({ ...form, layer: e.target.value })} placeholder="Layer" data-testid="sym-layer" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Input value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} placeholder="Block" data-testid="sym-block" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" data-testid="sym-desc" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Button size="sm" onClick={add} data-testid="sym-add-btn" className="h-8 rounded-[3px] bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-2"><Plus size={15} /></Button>
        </div>
        <ScrollArea className="h-[240px] border border-[var(--border)] rounded-[3px]">
          {items.length === 0 && <div className="p-3 text-xs text-[var(--text-dim)]">No symbols yet. Add your survey codes above.</div>}
          {items.map((s) => (
            <div key={s.id} className="grid grid-cols-[1fr_1.2fr_1.2fr_1.6fr_auto] gap-2 items-center px-2 py-1.5 border-b border-[var(--border)]/50 text-[12px] font-mono" data-testid={`sym-row-${s.code}`}>
              <span className="text-[var(--primary)] font-bold">{s.code}</span>
              <span className="truncate">{s.layer || "—"}</span>
              <span className="truncate">{s.block || "—"}</span>
              <span className="truncate text-[var(--text-dim)]">{s.description || "—"}</span>
              <button onClick={() => del(s.id, s.code)} data-testid={`sym-del-${s.code}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><Trash size={14} /></button>
            </div>
          ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
